import isValid from 'date-fns/isValid'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import startOfDay from 'date-fns/startOfDay'
import differnceInHours from 'date-fns/differenceInHours'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import endOfDay from 'date-fns/endOfDay'
import isFuture from 'date-fns/isFuture'
import utcToZonedTime from 'date-fns-tz/utcToZonedTime'
import tzFormat from 'date-fns-tz/format'
import moment from 'moment-timezone'
import { DateError } from './errors/DateError'
import { formatPlural } from './string'

export type RangeValues = { start?: string | null; end?: string | null }

const getUserTimezone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

export const isValidIsoDate = (value?: string): boolean => {
  if (typeof value !== 'string') return false
  const date = new Date(value)
  return isValid(date)
}

// NOTE: will not parse ISO strings
export const isValidDateObject = (value: unknown): value is Date => {
  if (typeof value === 'string') return false
  return isValid(value)
}

export const stringToUTC = (value: unknown): string => {
  try {
    if (typeof value !== 'string') throw new Error()
    return parseISO(value).toUTCString()
  } catch (e) {
    throw new DateError('INVALID_DATE')
  }
}

const parseUtcToDate = (utcValue: string): Date => {
  const date = parseISO(utcValue)
  if (!isValidDateObject(date)) throw new DateError('INVALID_DATE')
  return date
}

export const parseUtcDatetimeToUtcTime = (utcValue: string): string => {
  parseUtcToDate(utcValue)
  return utcValue.split('T')[1]
}

/**
 * Formats utc string date to `M/d/yyyy` format.
 *
 * formatting documentation: [date-fns:format](https://date-fns.org/v2.17.0/docs/format)
 * @param value
 */
export const utcToDisplayDate = (
  value: string,
  timeZone?: string | null | undefined,
  format?: string | null | undefined
): string => {
  try {
    timeZone ??= 'UTC'
    return moment(value)
      .tz(timeZone)
      .format(format || 'MM/DD/YYYY')
  } catch (e) {
    if (e instanceof DateError) {
      return ''
    }
    throw e
  }
}

export const utcToMilitaryDisplayTime = (
  value: string,
  timeZone?: string | null | undefined
): string => {
  try {
    timeZone ??= 'UTC' // a valid timezone is required for `moment.tz`, most clients are in CA but that will change so falling back to UTC
    return moment(value).tz(timeZone).format('HH:mm:ss z')
  } catch (e) {
    if (e instanceof DateError) {
      return ''
    }
    throw e
  }
}

export const msToHr = (msValue: number): number => Math.floor(msValue / 3600000)

export const startOfDayUTC = (date: Date): string => startOfDay(date).toISOString()

export const endOfDayUTC = (date: Date): string => endOfDay(date).toISOString()

export const isDateFuture = (date: Date): boolean => isFuture(date)

export const utcToTimezoneDate = (value: string, timeZone: string): Date =>
  utcToZonedTime(value, timeZone)

export const utcToDistanceDays = (value: string, toDate: string | null = null): string | null => {
  try {
    const date = utcToZonedTime(value, getUserTimezone())
    const now = utcToZonedTime(toDate ? new Date(toDate) : Date.now(), getUserTimezone())
    const hourDifference = differnceInHours(now, date)
    if (hourDifference <= 0) {
      const minDifference = differenceInMinutes(now, date)
      return `${minDifference} ${formatPlural({ value: 'min', count: minDifference })}`
    }
    if (hourDifference < 24) {
      return `${hourDifference} ${formatPlural({ value: 'hour', count: hourDifference })}`
    }
    const days = Math.floor(hourDifference / 24)
    const hrs = hourDifference - days * 24
    return `${days} ${formatPlural({ value: 'day', count: days })}, ${hrs} ${formatPlural({
      value: 'hr',
      count: hrs,
    })}`
  } catch (e) {
    return null
  }
}
